import { Button } from "@/components/ui/button";
import { ClipboardCopy, Loader2, Check } from "lucide-react";
import ReactMarkdown from 'react-markdown';
import remarkBreaks from "remark-breaks";
import { TextToSpeechButton } from "@/components/translate/tts";
import { MarkdownToggleButton } from "@/components/translate/markdown-toggle";
import { StarkySenseButton } from "./starky-sense-button";
import { StarkySenseResult } from "@/app/[locale]/api/starky-sense/route";
import { SelectedLanguage } from "@/components/translate/lang-dropdown";
import styles from './translate.module.css';
import { cn, copyToClipboard } from "@/lib/utils";
import { useEffect, useState, useRef } from "react";
import { useTranslations } from 'next-intl';
import { useTranslationRTL } from './rtl-context';
import { useRtl } from '@/utils/hooks/useRTL';
import { getHtmlLangFromSelectedLanguage } from '@/utils/language-conversion';
import type { useDynamicFontSize } from '@/utils/hooks/useDynamicFontSize';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";


interface OutputBoxProps {
  outputText: string;
  isBusy: boolean;
  showTranslateButton: boolean;
  lang: SelectedLanguage | undefined;
  onTranslate: () => void;
  dynamicFontSize: ReturnType<typeof useDynamicFontSize>;
  starkySenseResult?: StarkySenseResult | null;
  isStarkySenseLoading?: boolean;
  onShowStarkySenseDialog?: () => void;
  hasChanges?: boolean;
}

export function OutputBox({
  outputText,
  isBusy,
  showTranslateButton,
  lang,
  onTranslate,
  dynamicFontSize,
  starkySenseResult,
  isStarkySenseLoading,
  onShowStarkySenseDialog,
  hasChanges = false
}: OutputBoxProps) {

  useEffect(() => {
    if (copiedToClipboard !== outputText) {
      setCopiedToClipboard('');
    }
    // Reset small copy button success state when output changes
    setIsSmallCopySuccess(false);
  }, [outputText]);

  const [copiedToClipboard, setCopiedToClipboard] = useState('');
  const [isMarkdownEnabled, setIsMarkdownEnabled] = useState(false);
  const [isSmallCopySuccess, setIsSmallCopySuccess] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { outputDirection, outputTextAlign } = useTranslationRTL();
  const { direction } = useRtl();

  // Handle external text changes (translation results)
  useEffect(() => {
    dynamicFontSize.updateOutputText(outputText);
    dynamicFontSize.calculate();
  }, [outputText]);

  useEffect(() => {
    dynamicFontSize.updateMarkdownMode(isMarkdownEnabled);
    dynamicFontSize.calculate();
  }, [isMarkdownEnabled]);

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = dynamicFontSize.createResizeObserver(
      dynamicFontSize.updateOutputDimensions
    );

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [dynamicFontSize]);

  const handleCopyToClipboard = async () => {
    const success = await copyToClipboard(outputText);
    if (success) {
      setCopiedToClipboard(outputText);
    }
  };

  const handleSmallCopyToClipboard = async () => {
    const success = await copyToClipboard(outputText);
    if (success) {
      setIsSmallCopySuccess(true);
      setCopiedToClipboard(outputText);
      // Reset the success state after 2 seconds
      setTimeout(() => setIsSmallCopySuccess(false), 2000);
    }
  };

  const t = useTranslations('outputBox');

  return (
    <div ref={containerRef} className={`h-full grid grid-rows-[auto,1fr,auto] ${outputDirection}`}>
      <div className={`md:pt-0 mx-2 md:mx-0 transition-all duration-200 ease-in-out ${showTranslateButton
        ? 'opacity-100 translate-y-0 h-auto pt-2'
        : 'opacity-0 -translate-y-2 h-0 pt-0 pointer-events-none'
        }`}>
        {showTranslateButton && (
          <>
            {
              // Translate button
            }
            <Button
              className="w-full mb-2"
              onClick={onTranslate}>
              <p>
                <span className="hidden md:inline p-0 m-0">
                  {t.rich('translateButton.desktopHint', {
                    kbd: (chunks) => <kbd>{chunks}</kbd>
                  })}
                </span>
                {' ' + t('translateButton.text')}
              </p>
            </Button>
          </>
        )}
      </div>

      <div className={`${styles.translationOutputTextArea} relative flex flex-col ${outputDirection}`}>

        {/* There's a very good reason there's flex-[1_1_0] here and not flex-1, a bug in tw (the latter won't work in this context) */}
        {isMarkdownEnabled ? (
          <div
            className={`prose dark:prose-invert max-w-none [&>*]:text-foreground ${outputTextAlign} overflow-y-auto flex-[1_1_0] 
              [&>p]:mb-6 [&>h1]:my-3 [&>h2]:my-3 [&>h3]:my-2 [&>h4]:my-2 [&>h5]:my-2 [&>h6]:my-2 
              [&>ul]:my-0 [&>ol]:my-0 [&_li]:my-0 [&>blockquote]:my-2 [&>pre]:my-2 leading-normal`}
            style={{
              fontSize: `${dynamicFontSize.fontSize}px`,
              transition: 'font-size 0.2s ease-out'
            }}
            dir={outputDirection}
            lang={getHtmlLangFromSelectedLanguage(lang)}
          >
            <ReactMarkdown remarkPlugins={[remarkBreaks]}>
              {outputText}
            </ReactMarkdown>
          </div>
        ) : (
          <textarea
            value={outputText}
            readOnly
            className={cn(styles.translationTextArea, "flex-1")}
            style={{ fontSize: `${dynamicFontSize.fontSize}px` }}
            dir={outputDirection}
            lang={getHtmlLangFromSelectedLanguage(lang)}
          />
        )}

        {isBusy && (
          <div className="absolute inset-0 bg-background/50 backdrop-blur-[2px] flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        )}

        <div className="mt-2 flex gap-2 relative z-10" dir={direction}>
          <TextToSpeechButton
            text={outputText}
            language={lang?.name}
            disabled={isBusy}
          />

          <MarkdownToggleButton
            disabled={isBusy}
            onToggle={setIsMarkdownEnabled}
          />

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleSmallCopyToClipboard}
                  disabled={isBusy || !outputText?.trim()}
                  className={isSmallCopySuccess ? "bg-green-500 hover:bg-green-600" : ""}
                >
                  {isSmallCopySuccess ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <ClipboardCopy className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t('copyButtonTooltip')}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Starky Sense button moved here and docked to the right */}
          {(isStarkySenseLoading || (starkySenseResult && starkySenseResult.problems.length > 0)) && (
            <StarkySenseButton
              isLoading={isStarkySenseLoading || false}
              hasProblems={(starkySenseResult?.problems?.length || 0) > 0}
              onShowDialog={onShowStarkySenseDialog || (() => { })}
              minimized={hasChanges}
            />
          )}
        </div>
      </div>

      <div className={`mx-2 md:mb-0 md:mx-0 transition-all duration-200 ease-in-out ${!copiedToClipboard && outputText && !isBusy
        ? 'opacity-100 translate-y-0 h-auto pt-2 mb-2'
        : 'opacity-0 -translate-y-2 h-0 pt-0 overflow-hidden pointer-events-none'
        }`}>
        {!copiedToClipboard && outputText && !isBusy && (
          <Button
            className="w-full"
            variant="outline"
            dir={direction}
            onClick={handleCopyToClipboard}>
            <ClipboardCopy /> {t('copyButton')}
          </Button>
        )}
      </div>
    </div>
  );
}